import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import CommonDataTable from "../../components/CommonDataTable";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { fetchASN } from "../../services/asnAPI/asn";
import { toast } from "../../components/AppToast";
import { formatToDisplayDate } from "../../utils/dateFormatter";
import { addTab, setActiveTab } from "../../redux/action/tab.action";
import { APPOINTMENT_CREATE_PRESET_STORAGE_KEY } from "../../services/appointment/appointment";
import {
  columnLabels,
  defaultVisibleFields,
  // getColumnWidth,
  getValueFormatter,
  getColumnLabel,
  // getAsnStatusLabel,
} from "./constants/asnListColumns";
import { usePermission } from "../../hooks/usePermission";

const startCase = (str) =>
  (str || "")
    .replace(/([A-Z]+)/g, " $1")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bAsn\b/gi, "ASN")
    .replace(/\bSku\b/gi, "SKU")
    .replace(/\bId\b/gi, "ID")
    .trim();

export default function AsnList({
  onViewAsn,
  refreshKey,
  onEditAsn,
  onAddDetailsAsn,
  showCreateButton = false,
  createButtonText = "Create New ASN",
  createButtonIcon = "bi-plus-lg",
  onCreateClick,
  onOpenGrn,
  onOpenAppointment,
  onAfterRefresh,
  onhyperlinkOpen
}) {
  const [allPossibleColumns, setAllPossibleColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tableSelection, setTableSelection] = useState({ ids: [], rows: [] });
  const dispatch = useDispatch();
  const tabs = useSelector((state) => state.tab.tabs);

  // console.log("columns>>>>>>>>>>>>>> asn.....................", allPossibleColumns);

  // ============================
  // PERMISSIONS (OPTION A)
  // ============================
  const { hasPermission } = usePermission();
  const canViewASN = hasPermission("ASNManagement", "VIEW");
  const canAddASN = hasPermission("ASNManagement", "ADD");
  const canUpdateASN = hasPermission("ASNManagement", "UPDATE");
  const canApproveASN = hasPermission("ASNManagement", "APPROVER");
  const canExportASN = hasPermission("ASNManagement", "EXPORT");
  const canImportASN = hasPermission("ASNManagement", "IMPORT");

  const hasTableSelection = tableSelection.ids.length > 0;

  const openAppointmentCreateFromSelection = useCallback(() => {
    const keys = tableSelection.rows
      .map((r) => String(r?.asnNumber ?? "").trim())
      .filter(Boolean);
    if (!keys.length) {
      toast.error(
        "Could not read ASN number from the selected row(s). Select at least one valid ASN row."
      );
      return;
    }
    try {
      sessionStorage.setItem(
        APPOINTMENT_CREATE_PRESET_STORAGE_KEY,
        JSON.stringify({
          AppointmentType: "Inbound",
          sourceKeys: [...new Set(keys)],
        })
      );
    } catch (e) {
      console.warn("sessionStorage preset failed", e);
    }
    const path = "/appointment";
    const existingIndex = tabs.findIndex((tab) => tab.path === path);
    if (existingIndex !== -1) {
      dispatch(setActiveTab(existingIndex));
    } else {
      dispatch(addTab({ title: "Appointment", path, content: "Appointment" }));
    }
    window.dispatchEvent(new CustomEvent("partner-portal:appointment-preset-updated"));
  }, [dispatch, tableSelection.rows, tabs]);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);




  // 🔹 ASN list filter field map (UI col -> backend field path from response)
  const ASN_FILTER_FIELD_MAP = {
    // Action column (no backend field)
    __actions: null,

    // 🔹 ASN header / basic info (asnDetails + root)
    asnNumber: "asnDetails.asnNumber",     // also root pe hai, but canonical asnDetails
    owner: "asnDetails.owner",
    grnNumber: "grnNumber",                // not in sample, assuming root
    buyerName: "buyerName",                // assuming root
    asnType: "asnType",                    // assuming root

    receiptStatus: "asnDetails.receiptStatus",
    totalPOValue: "asnDetails.totalPOValue",
    warehouseId: "asnDetails.warehouseId",
    asnCreatedate: "asnDetails.asnCreatedate",
    externalAsnNumber: "asnDetails.externalAsnNumber",

    // UI me "External ASN #" – abhi same field se map kar diya
    externalASNReciept: "asnDetails.externalAsnNumber",
    AppointmentKey: "appointmentKey",

    // 🔹 Status / source / ids
    status: "status",          // numeric status (1,2,...)
    source: "source",          // top-level source
    _id: "_id",

    // Audit (assuming root pe honge)
    createdBy: "createdBy",
    updatedAt: "updatedAt",
    updatedBy: "updatedBy",

    // 🔹 Ship-from (supplier) block
    supplierName: "shipFrom.supplierName",
    supplierAddressLine1: "shipFrom.supplierAddressLine1",
    supplierAddressLine2: "shipFrom.supplierAddressLine2",
    supplierCity: "shipFrom.supplierCity",
    supplierState: "shipFrom.supplierState",
    supplierCountry: "shipFrom.supplierCountry",
    supplierContact: "shipFrom.supplierContact",
    supplierEmail: "shipFrom.supplierEmail",

    // 🔹 Carrier block
    carrierName: "carrier.carrierName",
    carrierAddressLine1: "carrier.carrierAddressLine1",
    carrierAddressLine2: "carrier.carrierAddressLine2",
    carrierCity: "carrier.carrierCity",
    carrierState: "carrier.carrierState",
    carrierCountry: "carrier.carrierCountry",
    carrierReference: "carrier.carrierReference",
    carrierPhone: "carrier.carrierPhone",

    // 🔹 Load / vehicle block
    loadNumber: "load.loadNumber",
    driverName: "load.driverName",
    actualDepartureDate: "load.actualDepartureDate",
    expectedArrivalDate: "load.expectedArrivalDate",

    // Ye fields response me sample me nahi the, but logical path:
    actualArrivalDate: "load.actualArrivalDate",
    expectedDepartureDate: "load.expectedDepartureDate",

    // 🔹 Other dates / qty (likely asnDetails)
    closedDate: "asnDetails.closedDate",
    invoiceDate: "asnDetails.invoiceDate",
    totalReceivedQty: "asnDetails.totalReceivedQty",
  };

  // 🔹 Mapper: UI filters -> API filters for ASN
  function mapAsnUiFiltersToApi(filters = []) {
    if (!Array.isArray(filters)) return [];

    return filters
      .map((f) => {
        if (!f || !f.columnId) return null;

        const apiField = ASN_FILTER_FIELD_MAP[f.columnId] ?? f.columnId;

        // __actions ya koi null-mapped field skip kar do
        if (apiField === null) return null;

        return {
          columnId: apiField,   // backend field path
          operator: f.operator,
          value: f.value,
        };
      })
      .filter(Boolean);
  }


  // ============================
  // LOAD ASN LIST
  // ============================
  const loadList = useCallback(async (pageNo, pageSizeNo, filters = []) => {
    setLoading(true);
    try {
      // console.log("pageNo,pageSizeNo" ,pageNo,pageSizeNo);
      console.log("filters>>>>>>>>>>>>>>>>>>>>",);

      const apiFilters = mapAsnUiFiltersToApi(filters);

      const allres = await fetchASN(pageNo, pageSizeNo, apiFilters);
      // const allres = []

      const apiData = allres.data;
      const total = allres.totalCount;

      const fieldSet = new Set();

      const builtRows = (apiData || []).map((rec, idx) => {
        const h = rec.asnDetails || {};
        const ship = rec.shipFrom || {};
        const buyer = rec.shipTo || {};
        const carr = rec.carrier || {};
        const load = rec.load || {};
        const udf = rec.userDefined || {};
        const invoice = rec.invoice || {};

        const formatDateField = (dateValue) => formatToDisplayDate(dateValue);


        const asnCreatedRaw = h.asnCreatedate || rec.createdAt;
        const asnCreatedFormatted = asnCreatedRaw
          ? formatToDisplayDate(asnCreatedRaw, true, "24h")
          : "";

        const flat = {
          id: h.asnNumber || rec._id || idx,
          asnNumber: h.asnNumber || "",
          grnNumber: h.grnNumber || invoice?.grnNumber || rec?.grnNumber || "",

          grnId: rec?.grnId || null,
          ...h,
          ...ship,
          ...buyer,
          ...carr,
          ...load,
          ...udf,
          ...invoice,
          _id: rec._id,
          // status: getAsnStatusLabel(rec.status),
          receiptStatus: h.receiptStatus || rec.receiptStatus,


          asnCreatedate: asnCreatedFormatted,

          expectedDepartureDate: formatDateField(load.expectedDepartureDate),
          actualDepartureDate: formatDateField(load.actualDepartureDate),
          expectedArrivalDate: formatDateField(load.expectedArrivalDate),
          actualArrivalDate: formatDateField(load.actualArrivalDate),
          closedDate: formatDateField(h.closedDate),
          invoiceDate: formatDateField(invoice.invoiceDate),

          // After spreads so API `asnDetails` / root appointment fields win consistently
          AppointmentKey:
            rec.AppointmentKey ??
            rec.appointmentKey ??
            rec.appointment_key ??
            h.AppointmentKey ??
            h.appointmentKey ??
            h.appointment_key ??
            rec.appointment?.AppointmentKey ??
            rec.appointment?.appointmentKey ??
            "",
        };


        Object.keys(flat).forEach((k) => {
          if (k !== "id" && k !== "__raw") fieldSet.add(k);
        });

        return flat;
      });

      defaultVisibleFields.forEach((f) => fieldSet.add(f));
      fieldSet.delete("__actions");

      // Omit root `status` — `receiptStatus` is the canonical ASN status column (duplicate "Status" headers).
      const remaining = Array.from(fieldSet)
        .filter((f) => f !== "status" && !defaultVisibleFields.includes(f))
        .sort((a, b) => a.localeCompare(b));

      const orderedFields = [...defaultVisibleFields, ...remaining];

      const actionsColumn = {
        field: "__actions",
        headerName: columnLabels["__actions"] || "",
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <IconButton
            size="small"
            onClick={(e) => handleOpenMenu(e, params.row)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ),
      };

      const cols = [
        actionsColumn,
        ...orderedFields
          .filter((f) => f !== "__actions")
          .map((f) => ({
            field: f,
            headerName: getColumnLabel(f, startCase),
            // width: getColumnWidth(f),
            valueFormatter: getValueFormatter(f),
          })),
      ];

      setAllPossibleColumns(cols);
      setRows(builtRows);
      onAfterRefresh?.(apiData);
      setTotalCount(total);

    } catch (error) {
      toast.error(`Failed to load ASN data: ${error.message || "Unknown error"}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log("ASN page, pageSize", page, pageSize);

    loadList(page, pageSize);

  }, [loadList, page, pageSize, refreshKey]);


  const handleTableDataRefresh = useCallback(
    ({ filters = [], page: p = 0, pageSize: ps = pageSize } = {}) => {
      console.log("AsnList handleTableDataRefresh:", { filters, page: p, pageSize: ps });
      // local state update (optional, agar use karna ho)
      setPage(p);
      setPageSize(ps);
      // actual API call
      loadList(p, ps, filters);
    },
    [loadList, pageSize]
  );


  // cleanup hidden action column
  useEffect(() => {
    try {
      const key = 'columnVisibility_asn-table';
      const saved = JSON.parse(localStorage.getItem(key) || '{}');
      if (saved && saved.__actions === false) {
        delete saved.__actions;
        localStorage.setItem(key, JSON.stringify(saved));
      }
    } catch { }
  }, []);

  // ================================
  // MENU HANDLERS WITH PERMISSIONS
  // ================================
  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuRow(row);
  };
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  const handleView = () => {
    if (!canViewASN) {
      toast.error("You do not have VIEW permission");
      return handleCloseMenu();
    }
    onViewAsn?.(menuRow);
    handleCloseMenu();
  };

  const handleEdit = () => {
    if (!canUpdateASN) {
      toast.error("You do not have UPDATE permission");
      return handleCloseMenu();
    }
    onEditAsn?.(menuRow);
    handleCloseMenu();
  };

  const handleAddDetails = () => {
    if (!canUpdateASN) {
      toast.error("You do not have UPDATE permission");
      return handleCloseMenu();
    }
    onAddDetailsAsn?.(menuRow);
    handleCloseMenu();
  };

  const handleViewConfirm = () => {
    if (!canApproveASN) {
      toast.error("You do not have APPROVER permission");
      return handleCloseMenu();
    }
    onViewAsn?.(menuRow);
    handleCloseMenu();
  };

  // ================================
  // RENDER
  // ================================
  return (
    <>
      {loading && <div className="text-center py-3">Loading ASN data...</div>}

      <CommonDataTable
        id="asn-table"
        allPossibleColumns={allPossibleColumns}
        initialVisible={["__actions", ...defaultVisibleFields, "lineItemsCount"]}
        initialRows={rows}
        height={500}
        rowsPerPageOptions={[10, 25, 50, 100]}
        // onDataRefresh={loadList}
        checkboxSelection

        // EXPORT / IMPORT buttons (OPTION A)
        exportImportPath={"asn"}
        sampleFileUrl="/samples/asn_sample.xlsx"
        sampleFileName="asn"
        enableExportButton={canExportASN}
        enableImportButton={canImportASN}

        // CREATE BUTTON: with row selection, only "Create Appointment" (Inbound + selected ASNs)
        showCreateButton={canAddASN || hasTableSelection}
        createButtonText={
          hasTableSelection ? "Create Appointment" : createButtonText
        }
        createButtonIcon={
          hasTableSelection ? "bi-calendar-plus" : createButtonIcon
        }
        onCreateClick={
          hasTableSelection ? openAppointmentCreateFromSelection : onCreateClick
        }
        onSelectionChange={setTableSelection}


        onManualRefresh={async ({ filters = [], page: p = 0, pageSize: ps = pageSize }) => {
          await loadList(p, ps, filters);   // <-- Refresh icon will trigger this
        }}

        // GRN + Appointment Key (same pattern as Appointment list / GRN module)
        linkColumns={{
          grnNumber: {
            entity: "GRN",
            parseValues: (val) => {
              if (!val) return [];
              if (Array.isArray(val)) return val.filter(Boolean).map(String);
              return String(val).split(/[,\|]/).map(s => s.trim()).filter(Boolean);
            },
          },
          AppointmentKey: {
            entity: "Appointment",
            underline: "always",
            parseValues: (val) => {
              if (val == null || val === "") return [];
              if (Array.isArray(val)) return val.filter(Boolean).map(String);
              return String(val)
                .split(/[,\|]/)
                .map((s) => s.trim())
                .filter(Boolean);
            },
          },
        }}

        onLinkOpen={({ field, value, row }) => {
          console.log("ASN Table ....................... onLinkOpen called:", { field, value, row });
          if (field === "grnNumber" && value) {
            onOpenGrn?.({
              grnNumber: (value),           // ✅ number (backup / UI)
            });
          }
          if (field === "AppointmentKey" && value) {
            onOpenAppointment?.(String(value).trim());
          }
        }}

        page={page}
        pageSize={pageSize}
        rowCount={totalCount}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(0);
        }}

        forInventory={true}               // 🔄 shows refresh button
        showSyncWms

        onDataRefresh={async ({ filters = [], page: p = 0, pageSize: ps = pageSize } = {}) => {
          // NOTE: this just forwards the values to your fetchHandler. For now we console.log as requested.
          console.log("ASN ....................... received onDataRefresh:", { filters, page: p, pageSize: ps });
          // call your existing fetchHandler (we'll update it to accept filters)
          await loadList(p, ps, filters);
        }}
      />

      {/* ACTION MENU */}
      <Menu
        open={Boolean(menuAnchorEl)}
        anchorEl={menuAnchorEl}
        onClose={handleCloseMenu}
      >
        {/* View / View Confirm (Pending) */}
        <MenuItem
          onClick={
            menuRow?.receiptStatus === "Pending"
              ? handleViewConfirm
              : handleView
          }
        >
          <i className="bi bi-eye me-2"></i>
          {menuRow?.receiptStatus === "Pending" ? "View / Confirm" : "View"}
        </MenuItem>

        {/* Add Details (only if status NEW) */}
        {menuRow?.receiptStatus === "New" && (
          <MenuItem onClick={handleAddDetails}>
            <i className="bi bi-file-earmark-plus me-2"></i>
            Add Details
          </MenuItem>
        )}

        {/* Edit (only Pending) */}
        {menuRow?.receiptStatus === "Pending" && (
          <MenuItem onClick={handleEdit}>
            <i className="bi bi-pencil me-2"></i>
            Edit
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
